import { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, CameraOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getCameraAccessFailure, requestUserCameraStream, type CameraAccessFailure } from '@/lib/cameraAccess';

interface CameraCaptureProps {
  isRecording: boolean;
  fps: number;
  onFrameCaptured: (frame: CapturedFrame) => void;
  onError: (error: string) => void;
  shouldInitialize?: boolean;
  onVideoElementReady?: (video: HTMLVideoElement | null) => void;
}

export interface CapturedFrame {
  frame_number: number;
  timestamp: number;
  base64_image: string;
}

export function CameraCapture({
  isRecording,
  fps,
  onFrameCaptured,
  onError,
  shouldInitialize = false,
  onVideoElementReady,
}: CameraCaptureProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const intervalRef = useRef<number | null>(null);
  const requestInFlightRef = useRef(false);
  const cameraGenerationRef = useRef(0);
  const enabledRef = useRef(shouldInitialize);
  enabledRef.current = shouldInitialize;
  const onErrorRef = useRef(onError);
  const onFrameCapturedRef = useRef(onFrameCaptured);
  const onVideoElementReadyRef = useRef(onVideoElementReady);
  const startTimeRef = useRef<number>(0);
  const [frameCount, setFrameCount] = useState(0);
  const [cameraReady, setCameraReady] = useState(false);
  const [permissionState, setPermissionState] = useState<'idle' | 'requesting' | 'granted' | 'denied'>('idle');
  const [cameraFailure, setCameraFailure] = useState<CameraAccessFailure | null>(null);

  onErrorRef.current = onError;
  onFrameCapturedRef.current = onFrameCaptured;
  onVideoElementReadyRef.current = onVideoElementReady;

  // CameraCapture owns the preview element. Expose that exact element to any
  // recording compositor so display, vision sampling and replay recording all
  // consume the same camera source.
  const assignVideoElement = useCallback((video: HTMLVideoElement | null) => {
    videoRef.current = video;
    onVideoElementReadyRef.current?.(video);
  }, []);

  // Request camera access function
  const requestCameraAccess = useCallback(async () => {
    if (requestInFlightRef.current || !enabledRef.current) return;
    const generation = cameraGenerationRef.current;
    requestInFlightRef.current = true;
    setPermissionState('requesting');
    setCameraFailure(null);
    
    try {
      const stream = await requestUserCameraStream({
        width: { ideal: 1280 },
        height: { ideal: 720 },
        facingMode: { ideal: 'user' },
      });
      
      if (!enabledRef.current || generation !== cameraGenerationRef.current) {
        stream.getTracks().forEach(track => track.stop());
        return;
      }
      // Store stream first
      streamRef.current = stream;
      
      // Update state unconditionally - this triggers re-render and video element appears
      setPermissionState('granted');
      setCameraReady(true);
      
      // Video element will be assigned in useEffect after re-render
    } catch (err) {
      if (!enabledRef.current || generation !== cameraGenerationRef.current) return;
      const failure = getCameraAccessFailure(err);
      console.warn(`Camera unavailable (${failure.code})`, err);
      setCameraFailure(failure);
      setPermissionState('denied');
      onErrorRef.current(failure.message);
    } finally {
      if (generation === cameraGenerationRef.current) requestInFlightRef.current = false;
    }
  }, []);

  const captureFrame = useCallback((frameNumber: number) => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    const timestamp = (Date.now() - startTimeRef.current) / 1000;
    onFrameCapturedRef.current({
      frame_number: frameNumber,
      timestamp,
      base64_image: canvas.toDataURL('image/jpeg', 0.8),
    });
    setFrameCount(prev => prev + 1);
  }, []);

  const startCapture = useCallback(() => {
    const intervalMs = 1000 / fps;
    let frameNum = 0;

    intervalRef.current = window.setInterval(() => {
      captureFrame(frameNum);
      frameNum++;
    }, intervalMs);
  }, [captureFrame, fps]);

  const stopCapture = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Assign stream to video element when it becomes available after permission granted
  useEffect(() => {
    if (permissionState === 'granted' && streamRef.current && videoRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [permissionState]);

  // Initialize camera only when shouldInitialize is true
  useEffect(() => {
    if (!shouldInitialize) {
      setCameraReady(false);
      setPermissionState('idle');
      return;
    }
    requestCameraAccess();

    // Cleanup
    return () => {
      cameraGenerationRef.current += 1;
      requestInFlightRef.current = false;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [requestCameraAccess, shouldInitialize]);

  // Start/stop frame capture based on recording state
  useEffect(() => {
    if (shouldInitialize && isRecording && cameraReady) {
      startTimeRef.current = Date.now();
      setFrameCount(0);
      startCapture();
    } else {
      stopCapture();
    }

    return () => stopCapture();
  }, [shouldInitialize, cameraReady, isRecording, startCapture, stopCapture]);

  if (!shouldInitialize) {
    return (
      <div className="flex items-center justify-center h-full w-full bg-muted/30 rounded-lg">
        <div className="text-center p-6">
          <CameraOff className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            Camera not initialized
          </p>
        </div>
      </div>
    );
  }

  if (permissionState === 'requesting') {
    return (
      <div className="flex items-center justify-center h-full w-full bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg">
        <div className="text-center p-6">
          <Camera className="w-12 h-12 text-blue-600 dark:text-blue-400 mx-auto mb-3 animate-pulse" />
          <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">Requesting Camera Access</h3>
          <p className="text-sm text-blue-700 dark:text-blue-300">
            Please allow camera access in your browser prompt...
          </p>
        </div>
      </div>
    );
  }

  if (permissionState === 'denied') {
    return (
      <div className="flex items-center justify-center h-full w-full p-6 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg">
        <div className="text-center max-w-md">
          <CameraOff className="w-12 h-12 text-amber-600 dark:text-amber-400 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-amber-900 dark:text-amber-100 mb-2">
            {cameraFailure?.title || 'Camera Access Required'}
          </h3>
          <p className="text-sm text-amber-700 dark:text-amber-300 mb-4">
            {cameraFailure?.message || 'To enable vision analysis, please allow camera access:'}
          </p>
          <ol className="text-left text-sm text-amber-700 dark:text-amber-300 mb-4 space-y-2">
            {(cameraFailure?.guidance || [
              'Open the site controls beside the browser address.',
              'Set Camera permission to Allow.',
              'Click “Try Again” after changing the permission.',
            ]).map((step, index) => <li key={step}>{index + 1}. {step}</li>)}
          </ol>
          <div className="flex gap-2 justify-center">
            <Button onClick={requestCameraAccess} size="sm">
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full">
      {/* Video Preview */}
      <div className="relative h-full w-full bg-gray-900 rounded-lg overflow-hidden">
        <video
          ref={assignVideoElement}
          autoPlay
          playsInline
          muted
          className="absolute inset-0 w-full h-full object-cover mirror"
        />
        
        {/* Recording Indicator */}
        {isRecording && (
          <div className="absolute top-4 right-4 flex items-center gap-2 bg-red-600 text-white px-3 py-2 rounded-full animate-pulse">
            <div className="w-3 h-3 bg-white rounded-full"></div>
            <span className="text-sm font-medium">Recording</span>
          </div>
        )}

        {/* Frame Count */}
        {isRecording && (
          <div className="absolute bottom-4 left-4 bg-black/60 text-white px-3 py-1 rounded text-sm">
            Frames: {frameCount}
          </div>
        )}

        {/* Camera Ready Indicator */}
        {cameraReady && !isRecording && (
          <div className="absolute top-4 left-4 flex items-center gap-2 bg-green-600 text-white px-3 py-2 rounded-full">
            <Camera className="w-4 h-4" />
            <span className="text-sm font-medium">Camera Ready</span>
          </div>
        )}
      </div>

      {/* Hidden canvas for frame capture */}
      <canvas ref={canvasRef} className="hidden" />

      {/* Mirror CSS */}
      <style>{`
        .mirror {
          transform: scaleX(-1);
        }
      `}</style>
    </div>
  );
}
